import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { DataTableQuery } from '../components/DataTable';
import type { ListEnvelope } from './genexus-query';

/**
 * Cliente HTTP por entidade que a fábrica `createCrudHooks` consome.
 *
 * Tipagem explícita por feature mantém DTOs visíveis no IDE — `api.ts` de
 * cada entidade implementa esta interface contra os endpoints específicos.
 */
export interface CrudApi<TList, TDetail, TInput, TId = number> {
  list: (query: DataTableQuery) => Promise<ListEnvelope<TList>>;
  get: (id: TId) => Promise<TDetail>;
  create: (input: TInput) => Promise<TDetail>;
  update: (id: TId, input: TInput) => Promise<TDetail>;
  remove: (id: TId) => Promise<void>;
}

export interface CrudHooks<TList, TDetail, TInput, TId = number> {
  useList: (query: DataTableQuery) => UseQueryResult<ListEnvelope<TList>, Error>;
  useDetail: (id: TId | null | undefined) => UseQueryResult<TDetail, Error>;
  useCreate: () => UseMutationResult<TDetail, Error, TInput>;
  useUpdate: () => UseMutationResult<TDetail, Error, { id: TId; input: TInput }>;
  useDelete: () => UseMutationResult<void, Error, TId>;
}

export interface CreateCrudHooksOptions<TList, TDetail, TInput, TId = number> {
  /** Chave base usada para query cache. Ex.: `['positions']`. */
  queryKey: readonly string[];
  api: CrudApi<TList, TDetail, TInput, TId>;
}

/**
 * Fábrica de hooks CRUD padrão usando TanStack Query.
 *
 * - `useList(query)` cacheia por `[...queryKey, 'list', query]`.
 * - `useDetail(id)` é desligado quando `id` é nullish.
 * - Mutations invalidam `[...queryKey]` (lista + detalhe).
 */
export function createCrudHooks<TList, TDetail, TInput, TId = number>(
  options: CreateCrudHooksOptions<TList, TDetail, TInput, TId>,
): CrudHooks<TList, TDetail, TInput, TId> {
  const { queryKey, api } = options;

  return {
    useList: (query) =>
      useQuery({
        queryKey: [...queryKey, 'list', query],
        queryFn: () => api.list(query),
        placeholderData: (previous) => previous,
      }),

    useDetail: (id) =>
      useQuery({
        queryKey: [...queryKey, 'detail', id],
        queryFn: () => api.get(id as TId),
        enabled: id !== null && id !== undefined,
      }),

    useCreate: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (input: TInput) => api.create(input),
        onSuccess: () => qc.invalidateQueries({ queryKey }),
      });
    },

    useUpdate: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: ({ id, input }: { id: TId; input: TInput }) => api.update(id, input),
        onSuccess: () => qc.invalidateQueries({ queryKey }),
      });
    },

    useDelete: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (id: TId) => api.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey }),
      });
    },
  };
}
